import React, { useState } from "react";
import {
  Box, Paper, Tab, Tabs,
} from "@mui/material";
import {
  BusinessRounded, GroupsRounded, Inventory2Rounded, RateReviewRounded,
} from "@mui/icons-material";
import { IFASAdditionalReport, IFASReportFilter, ifasReportsService } from "../../../../../services/ifasReportsService";
import { ColumnDef, IFASReportTable } from "../components/IFASReportTable";
import { ReportStatusChip } from "../components/ReportPrimitives";
import { IfasReportKey } from "../../../../../utils/ifasAccess";
import { formatR } from "../../../../../utils/money";

interface Props {
  inventoryMovements: IFASAdditionalReport[]; suppliers: IFASAdditionalReport[];
  customers: IFASAdditionalReport[]; feedback: IFASAdditionalReport[];
  mortuaryTrends: IFASAdditionalReport[];
  filter: IFASReportFilter; loading: boolean;
  allowedReports: IfasReportKey[];
}

const columns: ColumnDef<IFASAdditionalReport>[] = [
  { id: "referenceNumber", label: "Reference", minWidth: 125 },
  { id: "name", label: "Name / Item", minWidth: 160 },
  { id: "category", label: "Category", minWidth: 120 },
  { id: "status", label: "Status", minWidth: 105, format: (v) => v == null || v === "" ? "-" : <ReportStatusChip label={v} /> },
  { id: "date", label: "Date", minWidth: 105 },
  { id: "quantity", label: "Quantity", align: "right" },
  { id: "amount", label: "Amount", align: "right", format: (v) => v == null ? "-" : formatR(v as number | string) },
];

export const AdditionalReportsTab: React.FC<Props> = (props) => {
  const [reportIndex, setReportIndex] = useState(0);

  const table = (title: string, subtitle: string, data: IFASAdditionalReport[], type: string) => (
    <IFASReportTable title={title} subtitle={subtitle} columns={columns} data={data} loading={props.loading} onExport={(fmt) => ifasReportsService.downloadReportExport(type, fmt, props.filter)} />
  );

  const additionalReports = [
    { key: "inventoryMovements", label: "Inventory movements", title: "Inventory Movement Audit, Picking, Dispatch & Returns", subtitle: "Transactional movement trail with balances, outstanding and variance quantities", data: props.inventoryMovements, type: "inventory-movements", icon: <Inventory2Rounded /> },
    { key: "suppliers", label: "Suppliers", title: "Supplier Utilisation Report", subtitle: "Supplier service counts and committed/actual amounts", data: props.suppliers, type: "supplier-utilisation", icon: <BusinessRounded /> },
    { key: "customers", label: "Customers", title: "Customer Demographics & Funeral Package Analysis", subtitle: "Customer/deceased demographics linked to selected funeral packages", data: props.customers, type: "customer-analysis", icon: <GroupsRounded /> },
    { key: "feedback", label: "Feedback", title: "After Funeral Feedback Report", subtitle: "Pending, requested and captured feedback by funeral", data: props.feedback, type: "feedback", icon: <RateReviewRounded /> },
  ].filter(({ key }) => props.allowedReports.includes(key as IfasReportKey));
  const selectedReport = additionalReports[Math.min(reportIndex, Math.max(additionalReports.length - 1, 0))];

  return <Box sx={{ pb: 3 }}>
    {selectedReport && <>
      <Paper elevation={0} sx={{ mb: 2, p: .75, borderRadius: "14px", border: "1px solid #E2E8F0", boxShadow: "0 6px 18px rgba(15,23,42,.04)" }}>
        <Tabs value={Math.min(reportIndex, additionalReports.length - 1)} onChange={(_, value) => setReportIndex(value)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 44, "& .MuiTabs-indicator": { display: "none" }, "& .MuiTab-root": { minHeight: 42, borderRadius: "10px", px: 2, py: 1, color: "#64748B", fontSize: 11, fontWeight: 800, textTransform: "none", gap: .75, transition: "all .2s ease", "& .MuiSvgIcon-root": { fontSize: 18 } }, "& .Mui-selected": { bgcolor: "#FFF3E8", color: "#C95600 !important" } }}>
          {additionalReports.map((report) => <Tab key={report.key} icon={report.icon} iconPosition="start" label={`${report.label} (${report.data.length})`} />)}
        </Tabs>
      </Paper>
      <Box>{table(selectedReport.title, selectedReport.subtitle, selectedReport.data, selectedReport.type)}</Box>
    </>}
  </Box>;
};
